import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { API } from "@/lib/admin-constants";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Calendar,
  MapPin,
  Gift,
  Film,
  Video,
  FileText,
  Settings,
  LogOut,
  Globe,
  ChevronRight,
  ChevronDown,
  Mail,
  Zap,
  X,
  ClipboardEdit,
  Layers,
  SlidersHorizontal,
  Link2,
  BarChart3,
  Handshake,
  Share2,
} from "lucide-react";

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [unreadEmails, setUnreadEmails] = useState(0);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/site-content`);
        if (res.data?.branding?.logo_url) {
          setLogoUrl(res.data.branding.logo_url);
        }
      } catch (e) {
        console.error("Failed to load logo:", e);
      }
    };
    fetchLogo();
  }, []);

  // Poll unread email count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/email/unread-count`, { withCredentials: true });
        setUnreadEmails(res.data.unread_count || 0);
      } catch (e) {
        console.error("Failed to fetch unread count:", e);
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 20000);
    return () => clearInterval(interval);
  }, []);

  // Auto-expand the category that contains the active route
  useEffect(() => {
    const path = location.pathname;
    const autoExpand = {};
    navGroups.forEach((group) => {
      const match = group.items.some((item) =>
        item.exact ? path === item.path : path.startsWith(item.path)
      );
      if (match) autoExpand[group.label] = true;
    });
    setExpanded((prev) => ({ ...prev, ...autoExpand }));
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const toggleGroup = (label) => {
    setExpanded((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const navGroups = [
    {
      label: "CRM",
      icon: Users,
      items: [
        { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
        { path: "/admin/pipeline", label: "Pipeline", icon: Users, badge: unreadEmails > 0 ? unreadEmails : null },
        { path: "/admin/campaigns", label: "Campaigns", icon: Mail },
        { path: "/admin/email-analytics", label: "Email Analytics", icon: BarChart3 },
        { path: "/admin/tryouts", label: "Tryouts & Offers", icon: CalendarDays },
        { path: "/admin/events", label: "Events Calendar", icon: Calendar },
        { path: "/admin/drip-sequences", label: "Drip Sequences", icon: Zap },
        { path: "/admin/sponsor-pipeline", label: "Sponsor Pipeline", icon: Handshake },
      ],
    },
    {
      label: "Website",
      icon: Globe,
      items: [
        { path: "/admin/site-content", label: "Homepage", icon: Globe, exact: true },
        { path: "/admin/pages", label: "Pages", icon: Layers },
        { path: "/admin/forms", label: "Forms", icon: ClipboardEdit },
        { path: "/admin/site-settings", label: "Site Settings", icon: SlidersHorizontal },
        { path: "/admin/content", label: "News & Content", icon: FileText },
        { path: "/admin/map-locations", label: "Map Locations", icon: MapPin },
        { path: "/admin/referrals", label: "Referral Program", icon: Gift },
        { path: "/admin/highlights", label: "Player Highlights", icon: Film },
        { path: "/admin/reeplayer-invites", label: "Reeplayer Invites", icon: Video },
      ],
    },
    {
      label: "Settings",
      icon: Settings,
      items: [
        { path: "/admin/teamsnap", label: "TeamSnap", icon: Share2 },
        { path: "/admin/settings", label: "Notifications", icon: Settings },
        { path: "/admin/templates", label: "Email Templates", icon: Mail },
        { path: "/admin/sources", label: "External Sources", icon: Link2 },
      ],
    },
  ];

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-white border-r border-cfc-border flex flex-col transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" data-testid="sidebar-logo-link">
            {logoUrl ? (
              <img src={logoUrl} alt="CFC" className="h-9 w-auto object-contain" />
            ) : (
              <span className="font-heading text-lg font-bold text-cfc-primary">CFC Admin</span>
            )}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setIsOpen(false)}
          >
            <X size={20} />
          </Button>
        </div>

        <Separator />

        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="admin-nav">
          {navGroups.map((group) => {
            const isGroupExpanded = expanded[group.label];
            const hasActiveChild = group.items.some((item) => isActive(item));

            return (
              <div key={group.label} className="mb-1">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors ${
                    hasActiveChild
                      ? "text-cfc-primary bg-cfc-cream"
                      : "text-cfc-text-muted hover:text-cfc-primary hover:bg-cfc-cream/50"
                  }`}
                  data-testid={`nav-group-${group.label.toLowerCase()}`}
                >
                  <group.icon size={15} />
                  {group.label}
                  {isGroupExpanded ? (
                    <ChevronDown size={14} className="ml-auto" />
                  ) : (
                    <ChevronRight size={14} className="ml-auto" />
                  )}
                </button>

                {isGroupExpanded && (
                  <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 border-cfc-border pl-3">
                    {group.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                          isActive(item)
                            ? "bg-cfc-primary text-white"
                            : "text-cfc-text-muted hover:bg-cfc-cream hover:text-cfc-primary"
                        }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <item.icon size={16} />
                        {item.label}
                        {item.badge && (
                          <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${
                            isActive(item) ? "bg-white/20 text-white" : "bg-red-500 text-white"
                          }`}>
                            {item.badge > 99 ? "99+" : item.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-cfc-border space-y-2">
          <div className="flex items-center gap-3 px-3 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-cfc-primary flex items-center justify-center text-white font-bold text-sm">
                {user?.name?.charAt(0) || "A"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-cfc-primary truncate">{user?.name}</p>
              <p className="text-xs text-cfc-text-muted truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-cfc-text-muted hover:text-cfc-destructive text-xs"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={15} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
